import { Component, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { PnsEventType } from '../../../core/models/pns-event';
import { EventsStore } from '../../../core/store/pns-filters.store';
import { DateUtils } from '../../../core/shared/utils/date.utils';
import { FiltersService } from '../../../core/services/filters.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'dnv-filters-modal',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatSelectModule,
    TranslatePipe,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './filters-modal.html',
  styleUrl: './filters-modal.scss',
})
export class FiltersModal {
  private readonly store = inject(EventsStore);
  private readonly dialogRef = inject(MatDialogRef<FiltersModal>);
  private readonly filtersService = inject(FiltersService);
  public readonly pnsEventTypeEnum = PnsEventType;
  public eventArray = Object.values(this.pnsEventTypeEnum);

  public filterForm = new FormGroup({
    eventType: new FormControl(),
    startDate: new FormControl(),
    endDate: new FormControl(),
    pns: new FormControl(),
    author: new FormControl(),
  });

  public pnsArray = toSignal(
    this.filterForm.get('pns')!.valueChanges.pipe(
      startWith(''),                      
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(search => this.filtersService.getReferenceNames(search))
    ),
    { initialValue: [] }
  );

  public authorArray = toSignal(
    this.filterForm.get('author')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(search => this.filtersService.getUserEmails(search))
    ),
    { initialValue:[] }
  );

  ngOnInit(): void {
    const currentFilters = this.store.filters();
    this.filterForm.patchValue({
      eventType: currentFilters.type,
      startDate: DateUtils.toInputFormat(currentFilters.executedAt?.from),
      endDate: DateUtils.toInputFormat(currentFilters.executedAt?.to),
      pns: currentFilters.referenceName,
      author: currentFilters.userEmail,
    });
  }

  applyFilters(): void {
    const formValues = this.filterForm.value;

    const executedAtFilter =
      formValues.startDate || formValues.endDate
        ? {
            from: formValues.startDate,
            to: formValues.endDate,
          }
        : undefined;

    this.store.updateFilters({
      type: formValues.eventType,
      executedAt: executedAtFilter,
      referenceName: formValues.pns,
      userEmail: formValues.author,
    });
    this.dialogRef.close();
  }

  resetFilters(): void {
    this.filterForm.reset();
    this.store.resetFilters();
  }

  cancelFiltersDialog(): void {
    this.dialogRef.close();
  }
}
